import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Badge from '@/components/ui/Badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// Mock data for comparison
const runOptions = [
  { value: 'run_001', label: 'run_001 - Le Petit Prince' },
  { value: 'run_002', label: 'run_002 - Guide de voyage' },
  { value: 'run_003', label: 'run_003 - Manuel technique' },
  { value: 'run_004', label: 'run_004 - Rapport annuel' },
  { value: 'run_005', label: 'run_005 - Histoire courte' },
];

const stepComparisonData = [
  { step: 'PDF Ingest', run1: 2, run2: 3, run3: 4 },
  { step: 'Image Process', run1: 15, run2: 12, run3: 25 },
  { step: 'Chapter Detect', run1: 3, run2: 2, run3: 5 },
  { step: 'TTS Synth', run1: 35, run2: 28, run3: 45 },
  { step: 'Audio Assembly', run1: 5, run2: 4, run3: 8 },
  { step: 'Credits Gen', run1: 1, run2: 1, run3: 2 },
  { step: 'QA Finalize', run1: 2, run2: 2, run3: 3 },
];

const comparisonTable = [
  { metric: 'Total Duration', run1: '45m 23s', run2: '32m 15s', run3: '58m 42s' },
  { metric: 'Status', run1: '✅ Complete', run2: '✅ Complete', run3: '❌ Failed' },
  { metric: 'Pages Processed', run1: '156', run2: '89', run3: '234' },
  { metric: 'Chapters Detected', run1: '18', run2: '12', run3: '28' },
  { metric: 'TTS Batches', run1: '89', run2: '52', run3: '--' },
  { metric: 'Event Count', run1: '234', run2: '187', run3: '156' },
  { metric: 'Slowest Step', run1: 'TTS (28m)', run2: 'TTS (18m)', run3: 'Image (25m)' },
  { metric: 'Bottleneck', run1: 'None', run2: 'None', run3: 'Stuck @ TTS' },
];

export default function TTSTelemetryComparePage() {
  const [selectedRuns, setSelectedRuns] = useState(['run_001', 'run_002', 'run_003']);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to="/tts/telemetry"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Telemetry
      </Link>

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Pipeline Run Comparison</h1>
        <p className="text-slate-500 mt-1">
          Compare multiple runs side-by-side to identify performance patterns and bottlenecks
        </p>
      </div>

      {/* Run Selection */}
      <Card>
        <CardBody>
          <div className="flex flex-wrap items-end gap-4">
            <Select
              label="Run 1"
              options={runOptions}
              value={selectedRuns[0]}
              onChange={(e) => setSelectedRuns([e.target.value, selectedRuns[1], selectedRuns[2]])}
              className="w-64"
            />
            <Select
              label="Run 2"
              options={runOptions}
              value={selectedRuns[1]}
              onChange={(e) => setSelectedRuns([selectedRuns[0], e.target.value, selectedRuns[2]])}
              className="w-64"
            />
            <Select
              label="Run 3"
              options={runOptions}
              value={selectedRuns[2]}
              onChange={(e) => setSelectedRuns([selectedRuns[0], selectedRuns[1], e.target.value])}
              className="w-64"
            />
            <Button>Compare</Button>
          </div>
        </CardBody>
      </Card>

      {/* Step Duration Comparison Chart */}
      <Card>
        <CardHeader title="Step Duration Comparison" subtitle="Minutes per pipeline step" />
        <CardBody>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stepComparisonData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" stroke="#94A3B8" fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="step"
                  stroke="#94A3B8"
                  fontSize={11}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E2E8F0',
                    borderRadius: '12px',
                  }}
                />
                <Legend />
                <Bar dataKey="run1" fill="#4AC3C3" name="Run 1" radius={[0, 4, 4, 0]} />
                <Bar dataKey="run2" fill="#F5C542" name="Run 2" radius={[0, 4, 4, 0]} />
                <Bar dataKey="run3" fill="#EF5350" name="Run 3" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardBody>
      </Card>

      {/* Comparison Table */}
      <Card>
        <CardHeader title="Detailed Comparison" />
        <CardBody className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Metric</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">
                    <Badge variant="primary">run_001</Badge>
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">
                    <Badge variant="secondary">run_002</Badge>
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">
                    <Badge variant="danger">run_003</Badge>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {comparisonTable.map((row) => (
                  <tr key={row.metric} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-700">{row.metric}</td>
                    <td className="px-4 py-3 text-slate-600">{row.run1}</td>
                    <td className="px-4 py-3 text-slate-600">{row.run2}</td>
                    <td className="px-4 py-3 text-slate-600">{row.run3}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

